import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { useMvas } from "@/hooks/useMvas";
import { isMvaOverdue } from "@shared/mvaTracker";
import { StartMvaDialog } from "@/components/prospects/StartMvaDialog";
import { RecordPanel } from "@/components/record/RecordFrame";
import { dayLabel, firstName, localYmd } from "@/components/record/recordFormat";
import { cn } from "@/lib/utils";
import { mvaForCompany } from "./mvaForCompany";

/** Who is building this prospect's MVA and by when; or when it went out; or a way to start one. */
export function MvaPanel({ company }: { company: { id: string; name: string; industry: string | null } }) {
  const open = useMvas("open");
  const delivered = useMvas("delivered");
  const [startOpen, setStartOpen] = useState(false);
  const today = localYmd(new Date());
  const mva = mvaForCompany(company.id, open.data ?? [], delivered.data ?? []);
  const late = mva.kind === "open" && isMvaOverdue(mva.row, today);

  return (
    <RecordPanel
      title="MVA"
      action={mva.kind !== "open" && !open.isLoading && (
        <button type="button" onClick={() => setStartOpen(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-text hover:underline">
          <Plus className="h-3 w-3" />Start MVA
        </button>
      )}
    >
      {open.isLoading || delivered.isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : open.isError || delivered.isError ? (
        <p className="text-[13px] text-muted-foreground">Couldn't load the MVA tracker.</p>
      ) : mva.kind === "open" ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
          <span className="rounded bg-muted px-1.5 py-px text-[11px] font-semibold">{mva.row.status}</span>
          <span>{mva.row.assigneeName ? `${firstName(mva.row.assigneeName)} is on it` : "Waiting for someone to pick it up"}</span>
          {mva.row.dueOn && (
            <span className={cn("tabular-nums text-muted-foreground", late && "font-semibold text-destructive")}>
              {late ? "was due " : "due "}{dayLabel(mva.row.dueOn, today)}
            </span>
          )}
          {mva.row.docUploadedAt && <span className="text-xs text-muted-foreground">MVA document uploaded</span>}
        </div>
      ) : mva.kind === "delivered" ? (
        <p className="text-[13px]">
          Delivered {dayLabel(mva.row.deliveredAt, today)}{mva.row.assigneeName ? ` by ${firstName(mva.row.assigneeName)}` : ""}
        </p>
      ) : (
        <p className="text-[13px] text-muted-foreground">No MVA started.</p>
      )}
      <StartMvaDialog
        open={startOpen}
        onOpenChange={setStartOpen}
        prospects={[{ id: company.id, name: company.name, industry: company.industry, mva: null }]}
        initialCompanyId={company.id}
      />
    </RecordPanel>
  );
}
